'use client';

import React, { createContext, useContext, useRef } from 'react';
import type { AriaButtonProps } from 'react-aria';
import { mergeProps, useButton } from 'react-aria';
import { IconDrag } from '@frameio/vapor-icons';
import styles from '../../Collections.module.css';

export const ListItemDragHandleContext =
  createContext<AriaButtonProps<'button'> | null>(null);

const useListItemDragHandle = () => {
  const context = useContext(ListItemDragHandleContext);
  if (!context) {
    throw new Error(
      'useListItemDragHandle must be used within a ListItemDragHandleContext'
    );
  }
  return context;
};

type ListItemDragHandleProps = React.HTMLAttributes<HTMLDivElement> & {
  icon?: React.ReactNode;
  isDisabled?: boolean;
};

export function ListItemDragHandle({
  icon = <IconDrag />,
  isDisabled: isDisabledProp,
  ...props
}: ListItemDragHandleProps) {
  const ref = useRef<HTMLDivElement>(null);
  const { onPress, isDisabled, ...context } = useListItemDragHandle();

  const { buttonProps } = useButton(
    {
      isDisabled: isDisabled || isDisabledProp,
      elementType: 'div',
      onPress,
    },
    ref
  );

  return (
    <div
      ref={ref}
      className={styles.dragHandle}
      data-drag-handle="true"
      {...mergeProps(context, props, buttonProps)}
    >
      {icon}
    </div>
  );
}
